import { useState, useEffect, useRef, type Dispatch, type SetStateAction } from 'react'
import { useParams, useNavigate, useSearchParams, useBlocker } from 'react-router-dom'
import { Printer, Clock, Paperclip, Download, Trash2, Send, X } from 'lucide-react'
import { service } from '../lib/api/service'
import type { JournalEntry, Notification, ATASpec, ATALineItem, Surcharge, APris, Project, StatusHistory, Attachment, AttachmentSend } from '../lib/api/types'
import { formatSEK, ataNettoTotal, nettoBelop } from '../utils/calculations'
import { statusInfo } from '../utils/statusUtils'
import StatusBadge from './StatusBadge'
import StatusPipeline from './StatusPipeline'
import MissingChips from './MissingChips'
import LineItemsGrid from './LineItemsGrid'

export default function ATADetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const sortOrder = parseInt(id ?? '', 10)

  const [loading,   setLoading]   = useState(true)
  const [saving,    setSaving]    = useState(false)
  const [saved,     setSaved]     = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [dirty,     setDirty]     = useState(false)

  const [journal,       setJournal]      = useState<Partial<JournalEntry> | null>(null)
  const [notification,  setNotif]        = useState<Notification | null>(null)
  const [ataSpec,       setAtaSpec]      = useState<ATASpec | null>(null)
  const [surcharges,    setSurcharges]   = useState<Surcharge[]>([])
  const [apriser,       setApriser]      = useState<APris[]>([])
  const [project,       setProject]      = useState<Project | null>(null)
  const [statusHistory, setStatusHistory] = useState<StatusHistory[]>([])
  const [attachments,   setAttachments]  = useState<Attachment[]>([])
  const [sends,         setSends]        = useState<AttachmentSend[]>([])
  const [showPrint,     setShowPrint]    = useState(false)

  const initialTab = Math.min(3, Math.max(0, parseInt(searchParams.get('tab') ?? '0', 10)))
  const [activeTab, setActiveTab] = useState(initialTab)
  const topbarRef  = useRef<HTMLDivElement>(null)
  const pipeRef    = useRef<HTMLDivElement>(null)

  useEffect(() => {
    Promise.all([
      service.getAllEntries(),
      service.getSurcharges(),
      service.getAPriser(),
      service.getProject(),
      service.getStatusHistory(sortOrder).catch(() => [] as StatusHistory[]),
      service.getAttachments(sortOrder).catch(() => [] as Attachment[]),
      service.getSends(sortOrder).catch(() => [] as AttachmentSend[]),
    ]).then(([entries, sc, ap, proj, hist, atts, snds]) => {
      const entry = entries.find(e => e.sortOrder === sortOrder)
      setStatusHistory(hist ?? [])
      setAttachments(atts ?? [])
      setSends(snds ?? [])
      setJournal(entry?.journal ?? {
        sortOrder, arbeteBeskrivning: '', arbeteDetaljer: '',
        orsak: '', paverkanTid: '', paverkanEkonomi: '', ansvarig: '',
        brevDagbok: '', grundkravBelopp: null,
        bestellarSynpunkter: '', faktureratDatum: '', faktureratBelopp: null,
        status: 'upplagd',
      })
      setNotif(entry?.notification ?? null)
      setAtaSpec(entry?.ataSpec ?? null)
      setSurcharges(sc)
      setApriser(ap)
      setProject(proj)
    }).finally(() => setLoading(false))
  }, [sortOrder])

  useEffect(() => {
    if (!dirty) return
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirty])

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && currentLocation.pathname !== nextLocation.pathname
  )
  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (window.confirm('Du har osparade ändringar. Vill du lämna utan att spara?')) {
        blocker.proceed()
      } else {
        blocker.reset()
      }
    }
  }, [blocker])

  if (loading || !journal) return <div className="loading"><div className="spinner" /></div>

  const updateJournal = (field: keyof JournalEntry, value: string | number | null) => {
    setDirty(true)
    setJournal(j => ({ ...j, [field]: value }) as Partial<JournalEntry>)
  }

  const handleSave = async () => {
    setSaving(true)
    setSaveError(null)
    try {
      const result = await service.saveJournalEntry({ ...journal, sortOrder } as Partial<JournalEntry>)
      setJournal(j => ({ ...j, id: result?.id ?? j?.id }) as Partial<JournalEntry>)
      if (notification?.id) await service.saveNotification(notification)
      if (ataSpec?.id)      await service.saveATASpec(ataSpec)
      setDirty(false)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const handleStatusChange = async (newStatus: string) => {
    const updated: Partial<JournalEntry> = { ...journal, status: newStatus }
    setJournal(updated)
    try {
      await service.saveJournalEntry({ ...updated, sortOrder } as Partial<JournalEntry>)
    } catch (e) {
      setJournal(journal)
      setSaveError(e instanceof Error ? e.message : String(e))
    }
  }

  const createNotification = async () => {
    const today = new Date().toISOString().slice(0,10)
    const newN  = await service.saveNotification({
      sortOrder,
      datum: today,
      omstandighet: journal.arbeteDetaljer ?? '',
      ataMastUtforas: false, ataBorUtforas: false,
      kompProjektering: false, overGransvarde: false,
      vasentligRubbning: false, tidsrubbning: false,
      uppskattadKostnad: null, svarsDatum: null,
      bestellarSvar: 'Inväntar besked', bestellarSvarTyp: null,
      bestellarSignatur: null, bestellarDatum: null,
    })
    setNotif(newN as Notification)
    if (journal.status === 'upplagd') handleStatusChange('anmald')
    setActiveTab(1)
  }

  const createSpec = async () => {
    const today = new Date().toISOString().slice(0,10)
    const newS  = await service.saveATASpec({
      sortOrder, datum: today,
      beskrivning: journal.arbeteDetaljer ?? '',
      momsRate: 0.25, lineItems: [],
    })
    setAtaSpec(newS as ATASpec)
    setActiveTab(2)
  }

  const belopp = ataSpec ? ataNettoTotal(ataSpec.lineItems ?? [], surcharges) : null
  const status = journal.status ?? 'upplagd'
  const nrFormatted = `ÄTA-${String(sortOrder).padStart(3,'0')}`

  const tabs = [
    {
      label: 'Journalpost',
      sub:   'Den interna journalraden',
      dot:   journal.arbeteBeskrivning ? 'green' : 'amber',
    },
    {
      label: 'Underrättelse',
      sub:   notification ? `Skickad ${notification.datum || '–'}` : 'Saknas',
      dot:   notification ? 'green' : 'amber',
    },
    {
      label: 'Specifikation',
      sub:   ataSpec ? formatSEK(belopp) : 'Saknas',
      dot:   ataSpec ? 'green' : 'amber',
    },
    {
      label: 'Bilagor',
      sub:   attachments.length ? `${attachments.length} st` : 'Inga',
      dot:   attachments.length ? 'green' : 'amber',
    },
  ]

  return (
    <div>
      {/* Sticky header */}
      <div className="detail-topbar" ref={topbarRef}>
        <div className="detail-breadcrumb">
          <button onClick={() => navigate('/')}>← Översikt</button>
          <span>/</span>
          <span style={{ fontFamily:'var(--mono)', fontSize:11 }}>{nrFormatted}</span>
        </div>
        <div className="detail-title-row">
          <div className="detail-title-left">
            <div className="detail-nr-row">
              <span className="detail-nr">{nrFormatted}</span>
              <StatusBadge status={status} small />
              <MissingChips
                saknarU={!notification}
                saknarSpec={!ataSpec && status !== 'upplagd'}
                invanarSvar={notification?.bestellarSvar === 'Inväntar besked'}
              />
            </div>
            <h1 className="detail-h1">
              {journal.arbeteBeskrivning || <span style={{ color:'var(--ink-faint)', fontWeight:400, fontStyle:'italic' }}>Ingen rubrik</span>}
            </h1>
            <div className="detail-meta">
              {journal.orsak && (
                <span className="detail-meta-item">
                  <span className="detail-meta-key">Orsak</span>
                  <span className="detail-meta-val">{journal.orsak}</span>
                </span>
              )}
              {journal.skapad && (
                <span className="detail-meta-item">
                  <span className="detail-meta-key">Skapad</span>
                  <span className="detail-meta-val">{journal.skapad}</span>
                </span>
              )}
              {notification?.datum && (
                <span className="detail-meta-item">
                  <span className="detail-meta-key">Anmäld</span>
                  <span className="detail-meta-val">{notification.datum}</span>
                </span>
              )}
              {journal.ansvarig && (
                <span className="detail-meta-item">
                  <span className="detail-meta-key">Ansvarig</span>
                  <span className="detail-meta-val">{journal.ansvarig}</span>
                </span>
              )}
            </div>
          </div>
          <div className="detail-actions">
            <button className="icon-btn btn-sm" onClick={() => setShowPrint(true)}>
              <Printer size={13} /> Skriv ut…
            </button>
            {notification && (
              <button className="icon-btn btn-sm" onClick={() => navigate(`/print/notification/${sortOrder}`)}>
                <Printer size={13} /> Brev
              </button>
            )}
            {ataSpec && (
              <button className="icon-btn btn-sm" onClick={() => navigate(`/print/ata/${sortOrder}`)}>
                <Printer size={13} /> Spec
              </button>
            )}
            {dirty && !saving && (
              <span style={{ fontSize: 11, color: 'var(--ink-faint)', whiteSpace: 'nowrap' }}>
                ● Osparade ändringar
              </span>
            )}
            <button
              className="btn-primary btn-sm"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Sparar…' : saved ? 'Sparat ✓' : 'Spara'}
            </button>
          </div>
        </div>
      </div>

      {/* Status pipeline */}
      <div className="pipeline-strip" ref={pipeRef}>
        <StatusPipeline status={status} onChange={handleStatusChange} />
      </div>

      {/* Status/data mismatch warnings */}
      {['prissattning','prissatt','overlamnad','godkand','fakturerad'].includes(status) && !ataSpec && (
        <div className="notification notification-warn" style={{ margin: '0 0 0 0', borderRadius: 0, borderLeft: 'none', borderRight: 'none' }}>
          <span>Status är <strong>{statusInfo(status).kort}</strong> men ingen ÄTA-specifikation har skapats.</span>
        </div>
      )}

      {/* Tabs */}
      <div className="detail-tabs-bar">
        {tabs.map((tab, i) => (
          <button
            key={i}
            className={`detail-tab${activeTab === i ? ' active' : ''}`}
            onClick={() => setActiveTab(i)}
            role="tab"
            aria-selected={activeTab === i}
          >
            <span className="detail-tab-label">{tab.label}</span>
            <span className="detail-tab-sub">
              <span className={`tab-dot ${tab.dot}`} />
              {tab.sub}
            </span>
          </button>
        ))}
      </div>

      {/* Notifications */}
      {saved && (
        <div style={{ padding:'0 32px' }}>
          <div className="notification notification-success"><span>Sparat.</span></div>
        </div>
      )}
      {saveError && (
        <div style={{ padding:'0 32px' }}>
          <div className="notification notification-error"><span>Fel: {saveError}</span></div>
        </div>
      )}

      {/* Tab content */}
      <div className="detail-body">
        {activeTab === 0 && (
          <JournalpostTab
            journal={journal}
            update={updateJournal}
            statusHistory={statusHistory}
          />
        )}
        {activeTab === 1 && (
          <UnderrattelseTab
            notification={notification}
            setNotif={(fn) => { setDirty(true); setNotif(fn) }}
            journal={journal}
            project={project}
            sortOrder={sortOrder}
            createNotification={createNotification}
            handleSave={handleSave}
          />
        )}
        {activeTab === 2 && (
          <SpecTab
            ataSpec={ataSpec}
            setAtaSpec={(fn) => { setDirty(true); setAtaSpec(fn) }}
            surcharges={surcharges}
            apriser={apriser}
            createSpec={createSpec}
            handleSave={handleSave}
          />
        )}
        {activeTab === 3 && (
          <BilagorTab
            sortOrder={sortOrder}
            project={project}
            attachments={attachments}
            setAttachments={setAttachments}
            sends={sends}
            setSends={setSends}
          />
        )}
      </div>

      {showPrint && (
        <PrintModal
          sortOrder={sortOrder}
          hasNotification={!!notification}
          hasSpec={!!ataSpec}
          onClose={() => setShowPrint(false)}
        />
      )}
    </div>
  )
}

// ── Print selection modal (#12356) ─────────────────────────────────────────────

interface PrintModalProps {
  sortOrder: number
  hasNotification: boolean
  hasSpec: boolean
  onClose: () => void
}

function PrintModal({ sortOrder, hasNotification, hasSpec, onClose }: PrintModalProps) {
  const navigate = useNavigate()
  const [inc, setInc] = useState<Set<string>>(
    () => new Set([hasNotification ? 'notification' : 'ata']),
  )

  const toggle = (key: string) =>
    setInc(s => {
      const next = new Set(s)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const options: { key: string; label: string; sub: string; disabled: boolean }[] = [
    { key: 'notification', label: 'Underrättelse', sub: 'Det formella brevet (ABT 06)', disabled: !hasNotification },
    { key: 'spec',         label: 'Specifikation', sub: 'Prissatt ÄTA-spec', disabled: !hasSpec },
    { key: 'ata',          label: 'Endast ÄTA',    sub: 'Sammanfattning av journalposten', disabled: false },
  ]

  const submit = () => {
    const list = options.filter(o => !o.disabled && inc.has(o.key)).map(o => o.key)
    if (list.length === 0) return
    navigate(`/print/ata/${sortOrder}/combined?inc=${list.join(',')}`)
  }

  return (
    <div className="modal-scrim" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2 style={{ fontSize: 17, fontWeight: 600 }}>Skriv ut</h2>
          <button className="icon-button" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="field-hint" style={{ marginBottom: 12 }}>Välj vad som ska ingå i utskriften.</div>
          <div style={{ border: '.5px solid var(--border)', borderRadius: 8, padding: '4px 0' }}>
            {options.map(o => (
              <label
                key={o.key}
                style={{
                  display: 'flex', gap: 10, alignItems: 'center', padding: '9px 14px',
                  cursor: o.disabled ? 'not-allowed' : 'pointer', fontSize: 13,
                  opacity: o.disabled ? 0.45 : 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={!o.disabled && inc.has(o.key)}
                  disabled={o.disabled}
                  onChange={() => toggle(o.key)}
                  style={{ accentColor: 'var(--accent)', width: 15, height: 15 }}
                />
                <span style={{ flex: 1, fontWeight: 500 }}>{o.label}</span>
                <span style={{ fontSize: 11, color: 'var(--ink-faint)' }}>
                  {o.disabled ? 'Saknas' : o.sub}
                </span>
              </label>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="icon-btn" onClick={onClose}>Avbryt</button>
          <button className="btn-primary" onClick={submit} disabled={inc.size === 0}>
            Förhandsgranska
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Tab 1: Journalpost ────────────────────────────────────────────────────────

interface JournalpostTabProps {
  journal: Partial<JournalEntry>
  update: (field: keyof JournalEntry, value: string | number | null) => void
  statusHistory?: StatusHistory[]
}

function JournalpostTab({ journal, update, statusHistory = [] }: JournalpostTabProps) {
  return (
    <div className="detail-section">
      <div className="detail-section-header">
        <div>
          <div className="detail-section-eyebrow">01 · Journalpost</div>
          <div className="detail-section-title">Journalpost</div>
        </div>
      </div>

      <div className="grid-2" style={{ marginBottom:14 }}>
        <div className="field">
          <label className="field-label">Rubrik</label>
          <input
            className="field-input"
            value={journal.arbeteBeskrivning ?? ''}
            onChange={e => update('arbeteBeskrivning', e.target.value)}
            placeholder="Kort intern benämning"
          />
          <span className="field-hint">Visas i listor, brev och fakturareferens.</span>
        </div>
        <div className="field">
          <label className="field-label">Orsak</label>
          <select
            className="field-input"
            value={journal.orsak ?? ''}
            onChange={e => update('orsak', e.target.value)}
          >
            <option value="">Välj orsak…</option>
            {['Beställarens önskemål','Ändring av handling','Hinder','Myndighetsbeslut'].map(o => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="field" style={{ marginBottom:14 }}>
        <label className="field-label">Beskrivning</label>
        <textarea
          className="field-input"
          rows={4}
          value={journal.arbeteDetaljer ?? ''}
          onChange={e => update('arbeteDetaljer', e.target.value)}
          placeholder="Detaljerad intern beskrivning för platsledning."
        />
        <span className="field-hint">Intern text — skiljer sig från beställartexten i underrättelsen.</span>
      </div>

      <div className="grid-3" style={{ marginBottom:14 }}>
        <div className="field">
          <label className="field-label">Påverkan tid</label>
          <input
            className="field-input"
            value={journal.paverkanTid ?? ''}
            onChange={e => update('paverkanTid', e.target.value)}
            placeholder="Bedöms"
          />
        </div>
        <div className="field">
          <label className="field-label">Påverkan ekonomi</label>
          <input
            className="field-input"
            value={journal.paverkanEkonomi ?? ''}
            onChange={e => update('paverkanEkonomi', e.target.value)}
            placeholder="Bedöms"
          />
        </div>
        <div className="field">
          <label className="field-label">Ansvarig</label>
          <input
            className="field-input"
            value={journal.ansvarig ?? ''}
            onChange={e => update('ansvarig', e.target.value)}
            placeholder="Initialer"
          />
        </div>
      </div>

      {statusHistory.length > 0 && (
        <div style={{ background: 'var(--bg-2)', borderRadius: 8, padding: '12px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: 'var(--ink-dim)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 10 }}>
            <Clock size={12} /> Status-historik
          </div>
          {statusHistory.map((h, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, padding: '4px 0', borderBottom: i < statusHistory.length - 1 ? '.5px solid var(--border)' : 'none' }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-faint)', flexShrink: 0 }}>
                {h.changedAt ? String(h.changedAt).slice(0, 10) : '–'}
              </span>
              <span style={{ color: 'var(--ink-dim)' }}>{h.fromStatus ? statusInfo(h.fromStatus).kort : '–'}</span>
              <span style={{ color: 'var(--ink-faint)' }}>→</span>
              <span style={{ fontWeight: 500 }}>{statusInfo(h.toStatus).kort}</span>
            </div>
          ))}
        </div>
      )}

      <div className="fakt-card">
        <div className="fakt-card-title">Fakturering</div>
        <div className="grid-4">
          <div className="field">
            <label className="field-label">Fakturerat belopp (kr)</label>
            <input
              className="field-input mono"
              type="number"
              value={journal.faktureratBelopp ?? ''}
              onChange={e => update('faktureratBelopp', e.target.value === '' ? null : parseFloat(e.target.value))}
              placeholder="0"
            />
          </div>
          <div className="field">
            <label className="field-label">Fakturerat datum</label>
            <input
              className="field-input mono"
              type="date"
              value={journal.faktureratDatum ?? ''}
              onChange={e => update('faktureratDatum', e.target.value)}
            />
          </div>
          <div className="field">
            <label className="field-label">Brev / dagbok / BM</label>
            <input
              className="field-input"
              value={journal.brevDagbok ?? ''}
              onChange={e => update('brevDagbok', e.target.value)}
              placeholder="t.ex. BM 12, 2024-03-15"
            />
          </div>
          <div className="field">
            <label className="field-label">Grundkrav (kr)</label>
            <input
              className="field-input mono"
              type="number"
              value={journal.grundkravBelopp ?? ''}
              onChange={e => update('grundkravBelopp', e.target.value === '' ? null : parseFloat(e.target.value))}
              placeholder="0"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Tab 2: Underrättelse ──────────────────────────────────────────────────────

interface UnderrattelseTabProps {
  notification: Notification | null
  setNotif: Dispatch<SetStateAction<Notification | null>>
  journal?: Partial<JournalEntry>
  project: Project | null
  sortOrder: number
  createNotification: () => void
  handleSave?: () => void
}

function UnderrattelseTab({ notification, setNotif, project, sortOrder, createNotification }: UnderrattelseTabProps) {
  const navigate = useNavigate()
  const updateN = (field: keyof Notification, value: string | boolean) =>
    setNotif(n => (n ? { ...n, [field]: value } as Notification : n))

  if (!notification) {
    return (
      <div className="detail-section">
        <div className="saknas-card">
          <div className="saknas-card-icon">
            <svg width="32" height="32" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M8 2L14 13H2L8 2z" />
              <path d="M8 7v3M8 11.5v.5" strokeLinecap="round" />
            </svg>
          </div>
          <div className="saknas-card-title">Underrättelse saknas</div>
          <div className="saknas-card-desc">
            Ingen underrättelse har skapats för denna ÄTA. En underrättelse är det formella brevet till beställaren enligt ABT 06 kap. 2 § 6.
          </div>
          <div className="saknas-card-actions">
            <button className="btn-primary" onClick={createNotification}>
              Skapa underrättelse
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="detail-section">
      <div className="detail-section-header">
        <div>
          <div className="detail-section-eyebrow">02 · Underrättelse</div>
          <div className="detail-section-title">Underrättelse</div>
        </div>
        <div className="detail-section-actions">
          <button className="icon-btn btn-sm" onClick={() => navigate(`/print/notification/${sortOrder}`)}>
            <Printer size={13} /> Förhandsgranska brev
          </button>
        </div>
      </div>

      <div className="grid-2" style={{ marginBottom:14 }}>
        <div className="field">
          <label className="field-label">Skickad datum</label>
          <input
            className="field-input mono"
            type="date"
            value={notification.datum ?? ''}
            onChange={e => updateN('datum', e.target.value)}
          />
        </div>
        <div className="field">
          <label className="field-label">Mottagare</label>
          <input
            className="field-input"
            value={notification.bestellarSvar === 'Inväntar besked'
              ? (project?.bestRef ?? '')
              : (project?.bestRef ?? '')}
            disabled
            placeholder={project?.bestRef || project?.bestellare || '–'}
          />
        </div>
      </div>

      <div className="field" style={{ marginBottom:14 }}>
        <label className="field-label">Underrättelsetext</label>
        <textarea
          className="field-input"
          rows={6}
          value={notification.omstandighet ?? ''}
          onChange={e => updateN('omstandighet', e.target.value)}
          placeholder="Detta är texten som visas på det utgående brevet/PDF:en."
        />
        <span className="field-hint">Visas på det utgående brevet till beställaren.</span>
      </div>

      <div style={{ background:'var(--bg-2)', borderRadius:8, padding:'14px 16px' }}>
        <div style={{ fontSize:12, fontWeight:600, color:'var(--ink-dim)', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:10 }}>
          Bekräftelser
        </div>
        {([
          { key: 'ataMastUtforas',    label: 'ÄTA måste utföras',          sub: 'Formell markering' },
          { key: 'ataBorUtforas',     label: 'ÄTA bör utföras',            sub: '' },
          { key: 'vasentligRubbning', label: 'Väsentlig rubbning',         sub: 'av tidplan' },
          { key: 'tidsrubbning',      label: 'Tidsrubbning',               sub: '' },
        ] as { key: keyof Notification; label: string; sub: string }[]).map(row => (
          <label key={row.key} style={{ display:'flex', gap:10, alignItems:'center', marginBottom:8, cursor:'pointer', fontSize:13 }}>
            <input
              type="checkbox"
              checked={!!notification[row.key]}
              onChange={e => updateN(row.key, e.target.checked)}
              style={{ accentColor:'var(--accent)', width:15, height:15 }}
            />
            <span>{row.label}</span>
            {row.sub && <span style={{ color:'var(--ink-faint)', fontSize:11 }}>{row.sub}</span>}
          </label>
        ))}
      </div>
    </div>
  )
}

// ── Tab 3: Specifikation ──────────────────────────────────────────────────────

interface SpecTabProps {
  ataSpec: ATASpec | null
  setAtaSpec: Dispatch<SetStateAction<ATASpec | null>>
  surcharges: Surcharge[]
  apriser: APris[]
  createSpec: () => void
  handleSave?: () => void
}

function SpecTab({ ataSpec, setAtaSpec, surcharges, apriser, createSpec }: SpecTabProps) {
  const updateSpec = (field: keyof ATASpec, value: ATALineItem[]) =>
    setAtaSpec(s => (s ? { ...s, [field]: value } as ATASpec : s))

  if (!ataSpec) {
    return (
      <div className="detail-section">
        <div className="saknas-card">
          <div className="saknas-card-icon">
            <svg width="32" height="32" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M8 2L14 13H2L8 2z" />
              <path d="M8 7v3M8 11.5v.5" strokeLinecap="round" />
            </svg>
          </div>
          <div className="saknas-card-title">Specifikation saknas</div>
          <div className="saknas-card-desc">
            Ingen ÄTA-specifikation har skapats. En spec behövs för att prissätta arbetet med rader och à-priser.
          </div>
          <div className="saknas-card-actions">
            <button className="btn-primary" onClick={createSpec}>
              Skapa specifikation
            </button>
          </div>
        </div>
      </div>
    )
  }

  const lineItems = ataSpec.lineItems ?? []
  const nettoSumma = lineItems.reduce((s, item) => s + nettoBelop(item), 0)
  const activeSurcharges = surcharges.filter(s => s.typ?.trim())
  const total = ataNettoTotal(lineItems, surcharges)

  return (
    <div className="detail-section">
      <div className="detail-section-header">
        <div>
          <div className="detail-section-eyebrow">03 · Specifikation</div>
          <div className="detail-section-title">Specifikation</div>
        </div>
      </div>

      <LineItemsGrid
        lineItems={lineItems}
        surcharges={surcharges}
        apriser={apriser}
        onChange={items => updateSpec('lineItems', items)}
      />

      {/* Totals */}
      <div style={{ display:'flex', justifyContent:'flex-end', marginTop:16 }}>
        <div className="spec-totals">
          <div className="spec-totals-row">
            <span className="spec-totals-label">Summa rader</span>
            <span style={{ fontFamily:'var(--mono)' }}>{formatSEK(nettoSumma)}</span>
          </div>
          {activeSurcharges.map(s => {
            const base = lineItems
              .filter(i => i.paslagKod === s.sortOrder)
              .reduce((sum, i) => sum + nettoBelop(i), 0)
            const amount = base * s.procent
            if (amount === 0) return null
            return (
              <div key={s.sortOrder} className="spec-totals-row" style={{ color:'var(--ink-dim)', fontSize:12 }}>
                <span className="spec-totals-label">{s.typ}</span>
                <span style={{ fontFamily:'var(--mono)' }}>{formatSEK(amount)}</span>
              </div>
            )
          })}
          <div className="spec-totals-row total">
            <span>Total ÄTA</span>
            <span>{formatSEK(total)}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Tab 4: Bilagor (#12357) ───────────────────────────────────────────────────

interface BilagorTabProps {
  sortOrder: number
  project: Project | null
  attachments: Attachment[]
  setAttachments: Dispatch<SetStateAction<Attachment[]>>
  sends: AttachmentSend[]
  setSends: Dispatch<SetStateAction<AttachmentSend[]>>
}

function formatBytes(n: number | null): string {
  if (!n) return '–'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} kB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function BilagorTab({ sortOrder, project, attachments, setAttachments, sends, setSends }: BilagorTabProps) {
  const [uploading, setUploading] = useState(false)
  const [error, setError]   = useState<string | null>(null)
  const [showSend, setShowSend] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const refreshAttachments = async () =>
    setAttachments(await service.getAttachments(sortOrder))
  const refreshSends = async () =>
    setSends(await service.getSends(sortOrder))

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setUploading(true)
    setError(null)
    try {
      await service.uploadAttachments(sortOrder, files)
      await refreshAttachments()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const handleDownload = async (att: Attachment) => {
    try {
      const blob = await service.downloadAttachment(att.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = att.fileName
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  const handleDelete = async (att: Attachment) => {
    if (!window.confirm(`Ta bort "${att.fileName}"?`)) return
    setError(null)
    try {
      await service.deleteAttachment(att.id)
      await refreshAttachments()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <div className="detail-section">
      <div className="detail-section-header">
        <div>
          <div className="detail-section-eyebrow">04 · Bilagor</div>
          <div className="detail-section-title">Bilagor & underlag till kund</div>
        </div>
        <div className="detail-section-actions">
          <button
            className="btn-primary btn-sm"
            disabled={attachments.length === 0}
            onClick={() => setShowSend(true)}
          >
            <Send size={13} /> Skicka till kund
          </button>
        </div>
      </div>

      {error && (
        <div className="notification notification-error" style={{ marginBottom: 14 }}>
          <span>Fel: {error}</span>
        </div>
      )}

      {/* Upload */}
      <div style={{ marginBottom: 18 }}>
        <input
          ref={fileRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={e => handleUpload(e.target.files)}
        />
        <button
          className="icon-btn btn-sm"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
        >
          <Paperclip size={13} /> {uploading ? 'Laddar upp…' : 'Ladda upp bilaga'}
        </button>
        <span className="field-hint" style={{ marginLeft: 10 }}>
          Lagras säkert i Azure Blob. Skickas till kund som tidsbegränsade nedladdningslänkar.
        </span>
      </div>

      {/* Attachment list */}
      {attachments.length === 0 ? (
        <div className="field-hint" style={{ padding: '8px 0' }}>Inga bilagor uppladdade ännu.</div>
      ) : (
        <div style={{ border: '.5px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          {attachments.map((att, i) => (
            <div
              key={att.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                borderBottom: i < attachments.length - 1 ? '.5px solid var(--border)' : 'none',
                fontSize: 13,
              }}
            >
              <Paperclip size={14} style={{ color: 'var(--ink-faint)', flexShrink: 0 }} />
              <span style={{ flex: 1, fontWeight: 500 }}>{att.fileName}</span>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-faint)' }}>
                {formatBytes(att.sizeBytes)}
              </span>
              <span style={{ fontSize: 11, color: 'var(--ink-faint)', minWidth: 90 }}>
                {att.uploadedByEmail ?? '–'}
              </span>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-faint)' }}>
                {att.uploadedAt ? String(att.uploadedAt).slice(0, 10) : '–'}
              </span>
              <button className="icon-btn btn-sm" title="Ladda ner" onClick={() => handleDownload(att)}>
                <Download size={13} />
              </button>
              <button className="icon-btn btn-sm" title="Ta bort" onClick={() => handleDelete(att)}>
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Skickat-logg — the traceability record (#12357) */}
      <div style={{ marginTop: 24 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-dim)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 10 }}>
          Skickat till kund
        </div>
        {sends.length === 0 ? (
          <div className="field-hint">Inget underlag har skickats ännu.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sends.map(s => (
              <div key={s.id} style={{ background: 'var(--bg-2)', borderRadius: 8, padding: '12px 16px', fontSize: 12.5 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontWeight: 600 }}>{s.recipientEmail}</span>
                  <span className={`status-badge${s.status === 'failed' ? ' amber' : ''}`} style={{ fontSize: 10 }}>
                    {s.status === 'failed' ? 'Misslyckades' : 'Skickat'}
                  </span>
                  <span style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-faint)' }}>
                    {s.sentAt ? String(s.sentAt).slice(0, 16).replace('T', ' ') : '–'}
                  </span>
                </div>
                {s.subject && <div style={{ color: 'var(--ink-dim)', marginBottom: 4 }}>{s.subject}</div>}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 4 }}>
                  {s.items.map(it => (
                    <span key={it.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, background: 'var(--bg)', border: '.5px solid var(--border)', borderRadius: 5, padding: '2px 7px' }}>
                      <Paperclip size={10} /> {it.fileName ?? '–'}
                    </span>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-faint)' }}>
                  av {s.sentByEmail ?? '–'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showSend && (
        <SendModal
          sortOrder={sortOrder}
          attachments={attachments}
          defaultRecipient={project?.bestEmail ?? ''}
          onClose={() => setShowSend(false)}
          onDone={refreshSends}
        />
      )}
    </div>
  )
}

interface SendModalProps {
  sortOrder: number
  attachments: Attachment[]
  defaultRecipient: string
  onClose: () => void
  onDone: () => Promise<void>
}

function SendModal({ sortOrder, attachments, defaultRecipient, onClose, onDone }: SendModalProps) {
  const [selected, setSelected]   = useState<number[]>(attachments.map(a => a.id))
  const [recipient, setRecipient] = useState(defaultRecipient)
  const [subject, setSubject]     = useState('Underlag ÄTA')
  const [message, setMessage]     = useState('')
  const [sending, setSending]     = useState(false)
  const [error, setError]         = useState<string | null>(null)

  const toggle = (id: number) =>
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id])

  const submit = async () => {
    if (!recipient.trim() || selected.length === 0) {
      setError('Ange mottagare och välj minst en bilaga.')
      return
    }
    setSending(true)
    setError(null)
    try {
      await service.sendToCustomer(sortOrder, {
        recipientEmail: recipient.trim(), subject, message, attachmentIds: selected,
      })
      await onDone()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      await onDone()  // a failed attempt is still logged — refresh so it shows
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="modal-scrim" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2 style={{ fontSize: 17, fontWeight: 600 }}>Skicka underlag till kund</h2>
          <button className="icon-button" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {error && (
            <div className="notification notification-error" style={{ marginBottom: 14 }}>
              <span>{error}</span>
            </div>
          )}
          <div className="field" style={{ marginBottom: 14 }}>
            <label className="field-label">Mottagare (e-post)</label>
            <input
              className="field-input"
              type="email"
              value={recipient}
              onChange={e => setRecipient(e.target.value)}
              placeholder="kund@exempel.se"
            />
          </div>
          <div className="field" style={{ marginBottom: 14 }}>
            <label className="field-label">Ämne</label>
            <input className="field-input" value={subject} onChange={e => setSubject(e.target.value)} />
          </div>
          <div className="field" style={{ marginBottom: 14 }}>
            <label className="field-label">Meddelande</label>
            <textarea
              className="field-input"
              rows={4}
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Valfritt följebrev till kunden."
            />
          </div>
          <div className="field">
            <label className="field-label">Bilagor att skicka</label>
            <div style={{ border: '.5px solid var(--border)', borderRadius: 8, padding: '4px 0' }}>
              {attachments.map(att => (
                <label key={att.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '7px 14px', cursor: 'pointer', fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={selected.includes(att.id)}
                    onChange={() => toggle(att.id)}
                    style={{ accentColor: 'var(--accent)', width: 15, height: 15 }}
                  />
                  <Paperclip size={13} style={{ color: 'var(--ink-faint)' }} />
                  <span style={{ flex: 1 }}>{att.fileName}</span>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-faint)' }}>
                    {formatBytes(att.sizeBytes)}
                  </span>
                </label>
              ))}
            </div>
            <span className="field-hint">Kunden får tidsbegränsade nedladdningslänkar (giltiga 7 dagar).</span>
          </div>
        </div>
        <div className="modal-footer">
          <button className="icon-btn" onClick={onClose}>Avbryt</button>
          <button className="btn-primary" onClick={submit} disabled={sending}>
            {sending ? 'Skickar…' : 'Skicka'}
          </button>
        </div>
      </div>
    </div>
  )
}
